import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as api from '../../services/api';

type TiersMeData = {
  tier: string;
  allowed_services: string[];
  usage_this_month?: number;
  monthly_quota?: number;
  max_sub_accounts?: number;
  sub_account_count?: number;
  is_group_admin?: boolean;
  user_group_id?: number | null;
  user_role?: 'admin' | 'sub_account';
};

type SubAccountRow = {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  created_at: string | null;
  /** From user_credentials.is_mfa_required; when false, login does not require MFA. */
  is_mfa_required?: boolean;
};

// ----- Tabs -----
const TabsRow = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  background: #fff;
  padding: 0 24px 0 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 14px 20px;
  border: none;
  background: none;
  font-size: 15px;
  font-weight: 500;
  color: ${(p) => (p.$active ? '#4f46e5' : '#6b7280')};
  border-bottom: 2px solid ${(p) => (p.$active ? '#4f46e5' : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;
  &:hover {
    color: ${(p) => (p.$active ? '#4f46e5' : '#374151')};
  }
`;

// ----- Settings content area -----
const SettingsContent = styled.div`
  padding: 24px;
  background: #fff;
  min-height: 400px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  width: 280px;
  height: 40px;
  padding: 0 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  &:focus-within {
    border-color: #4f46e5;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 14px;
  outline: none;
  &::placeholder {
    color: #9ca3af;
  }
`;

const AddUserBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #1f2937;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #374151;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const QuotaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  color: #111827;
`;

const Tr = styled.tr`
  &:hover {
    background: #f9fafb;
  }
`;

const StatusBadge = styled.span<{ $active?: boolean }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${(p) => (p.$active ? '#d1fae5' : '#fee2e2')};
  color: ${(p) => (p.$active ? '#065f46' : '#991b1b')};
`;

const AccessBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: #dbeafe;
  color: #1e40af;
`;

// ----- Modal -----
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 24px;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #111827;
`;

const ModalClose = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: #6b7280;
  &:hover {
    background: #e5e7eb;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
  input, select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    box-sizing: border-box;
    &:focus {
      outline: none;
      border-color: #4f46e5;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
`;

const BtnSecondary = styled.button`
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #374151;
  &:hover {
    background: #f9fafb;
  }
`;

const BtnPrimary = styled.button`
  padding: 10px 20px;
  border: none;
  background: #4f46e5;
  color: #fff;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: #4338ca;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

type TabId = 'account' | 'users' | 'groups' | 'system' | 'agent_local' | 'patient_portal';

// ── Groups tab ────────────────────────────────────────────────────────────────
const SettingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 520px;
`;

const SettingLabel = styled.label`
  min-width: 160px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const SettingInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  color: #111827;
  &:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 2px #ede9fe; }
`;

const SettingHint = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: -8px 0 16px 172px;
  max-width: 360px;
`;

const SaveBtn = styled.button`
  padding: 8px 20px;
  background: #4f46e5;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover:not(:disabled) { background: #4338ca; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const GroupsTab: React.FC = () => {
  const [prefix, setPrefix] = useState('');
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    api.getGroupSettings()
      .then((res) => {
        if (res.success && res.data) {
          setGroupName(res.data.group_name);
          setPrefix(res.data.jane_app_prefix || '');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await api.updateGroupSettings({ jane_app_prefix: prefix.trim().toLowerCase() });
      setMsg({ text: res.message || (res.success ? 'Saved.' : 'Failed.'), ok: !!res.success });
    } catch {
      setMsg({ text: 'Failed to save.', ok: false });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading…</p>;

  return (
    <>
      <PageTitle>Groups</PageTitle>
      {groupName && (
        <p style={{ color: '#6b7280', marginBottom: 24 }}>
          Group: <strong>{groupName}</strong>
        </p>
      )}

      <SettingRow>
        <SettingLabel htmlFor="jane-prefix">JaneApp prefix</SettingLabel>
        <SettingInput
          id="jane-prefix"
          type="text"
          placeholder="e.g. thefncminnesota"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          spellCheck={false}
        />
      </SettingRow>
      <SettingHint>
        The subdomain of your JaneApp admin portal. For <code>thefncminnesota.janeapp.com/admin</code>, enter <code>thefncminnesota</code>.
      </SettingHint>

      <SaveBtn onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </SaveBtn>

      {msg && (
        <p style={{ marginTop: 12, color: msg.ok ? '#059669' : '#dc2626', fontSize: 14 }}>
          {msg.text}
        </p>
      )}
    </>
  );
};

// ── Local Agent styles ────────────────────────────────────────────────────────
const AgentCard = styled.div`
  max-width: 560px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
`;

const AgentCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const AgentDot = styled.span<{ $online: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => (p.$online ? '#16a34a' : '#9ca3af')};
  flex-shrink: 0;
`;

const AgentStatusLabel = styled.span<{ $online: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.$online ? '#15803d' : '#6b7280')};
`;

const AgentCardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const AgentRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TokenBox = styled.code`
  flex: 1;
  padding: 9px 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  font-size: 13px;
  color: #111827;
  letter-spacing: 0.03em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconBtn = styled.button`
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  white-space: nowrap;
  &:hover { background: #f9fafb; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const AgentDownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  background: linear-gradient(135deg, #0f172a, #1e293b);
  color: #f8fafc;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  width: fit-content;
  &:hover:not(:disabled) { background: linear-gradient(135deg, #1e293b, #334155); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const AgentMeta = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('account');
  const [tiersMe, setTiersMe] = useState<TiersMeData | null>(null);
  const [tokenStatus, setTokenStatus] = useState<api.TokenUsageStatus | null>(null);
  const [subAccounts, setSubAccounts] = useState<SubAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [agentStatus, setAgentStatus] = useState<api.McpAgentStatus | null>(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);

  const [createUsername, setCreateUsername] = useState('');
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createUserAccess, setCreateUserAccess] = useState('Full');
  const [createRoles, setCreateRoles] = useState('');
  const [createGroups, setCreateGroups] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createErr, setCreateErr] = useState('');
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [resettingPwId, setResettingPwId] = useState<number | null>(null);

  // Patient Portal tab state
  const [patients, setPatients] = useState<api.PortalPatient[]>([]);
  const [patientsLoaded, setPatientsLoaded] = useState(false);
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientDob, setPatientDob] = useState('');
  const [patientPassword, setPatientPassword] = useState('');
  const [patientCreateErr, setPatientCreateErr] = useState('');
  const [patientCreateSubmitting, setPatientCreateSubmitting] = useState(false);
  const [removingPatientUuid, setRemovingPatientUuid] = useState<string | null>(null);
  const [patientErr, setPatientErr] = useState('');
  const [shareLoginPatient, setShareLoginPatient] = useState<api.PortalPatient | null>(null);
  const [shareLoginPassword, setShareLoginPassword] = useState('');
  const [shareLoginSubmitting, setShareLoginSubmitting] = useState(false);
  const [shareLoginCopied, setShareLoginCopied] = useState(false);
  const [shareLoginErr, setShareLoginErr] = useState('');

  useEffect(() => {
    Promise.all([
      api.getTiersMe().catch(() => null),
      api.getSubAccounts().catch(() => null),
      api.getTokenUsageStatus().catch(() => null),
    ])
      .then(([tRes, sRes, tokRes]) => {
        if (tRes?.success && tRes?.data) setTiersMe(tRes.data);
        if (sRes?.success && sRes?.data?.sub_accounts) setSubAccounts(sRes.data.sub_accounts);
        if (tokRes) setTokenStatus(tokRes);
      })
      .finally(() => setLoading(false));
  }, []);

  // Sub-account role only sees Account tab; ensure we don't stay on Users/Groups/System
  useEffect(() => {
    if (tiersMe?.user_role === 'sub_account' && activeTab !== 'account' && activeTab !== 'agent_local' && activeTab !== 'patient_portal') {
      setActiveTab('account');
    }
  }, [tiersMe?.user_role, activeTab]);

  // Poll agent status when on Local Agent tab
  useEffect(() => {
    if (activeTab !== 'agent_local') return;
    setAgentLoading(true);
    api.getMcpStatus()
      .then((res) => setAgentStatus(res))
      .catch(() => {})
      .finally(() => setAgentLoading(false));
    const interval = setInterval(() => {
      api.getMcpStatus()
        .then((res) => setAgentStatus(res))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Add User modal belongs to Users tab only: close it when switching to another tab
  useEffect(() => {
    if (activeTab !== 'users') setAddUserOpen(false);
  }, [activeTab]);

  const refreshSubAccounts = () => {
    api.getSubAccounts()
      .then((res) => {
        if (res?.success && res?.data?.sub_accounts) setSubAccounts(res.data.sub_accounts);
      })
      .catch(() => {});
  };

  const loadPatients = () => {
    api.listPortalPatients()
      .then((res) => { setPatients(res.patients ?? []); setPatientsLoaded(true); })
      .catch(() => { setPatientsLoaded(true); });
  };

  useEffect(() => {
    if (activeTab === 'patient_portal' && !patientsLoaded) loadPatients();
  }, [activeTab, patientsLoaded]);

  const handleCreatePatient = (e: React.FormEvent) => {
    e.preventDefault();
    setPatientCreateErr('');
    if (!patientFirstName.trim() || !patientLastName.trim() || !patientDob.trim() || !patientPassword.trim()) {
      setPatientCreateErr('All fields are required.');
      return;
    }
    setPatientCreateSubmitting(true);
    api.createPortalPatient({
      first_name: patientFirstName.trim(),
      last_name: patientLastName.trim(),
      dob: patientDob.trim(),
      password: patientPassword,
    })
      .then((res) => {
        setPatients((prev) => [...prev, res.patient]);
        setAddPatientOpen(false);
        setPatientFirstName('');
        setPatientLastName('');
        setPatientDob('');
        // Open share modal pre-filled with the creation password so clinician can copy immediately
        setShareLoginPatient(res.patient);
        setShareLoginPassword(patientPassword);
        setShareLoginCopied(false);
        setShareLoginErr('');
        setPatientPassword('');
      })
      .catch((e: any) => setPatientCreateErr(e?.data?.message || e?.message || 'Failed to create patient'))
      .finally(() => setPatientCreateSubmitting(false));
  };

  const handleRemovePatient = (patientUuid: string, name: string) => {
    if (!window.confirm(`Remove patient "${name}"? This cannot be undone.`)) return;
    setRemovingPatientUuid(patientUuid);
    api.deletePortalPatient(patientUuid)
      .then(() => { setPatients((prev) => prev.filter((p) => p.patient_uuid !== patientUuid)); })
      .catch((e: any) => setPatientErr(e?.data?.message || e?.message || 'Failed to remove patient'))
      .finally(() => setRemovingPatientUuid(null));
  };

  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const handleOpenShareLogin = (patient: api.PortalPatient) => {
    setShareLoginPatient(patient);
    setShareLoginPassword(genPassword());
    setShareLoginCopied(false);
    setShareLoginErr('');
  };

  const handleShareLoginSubmit = () => {
    if (!shareLoginPatient || !shareLoginPassword) return;
    setShareLoginSubmitting(true);
    setShareLoginErr('');
    api.patientPortalResetPassword(shareLoginPatient.patient_uuid, shareLoginPassword)
      .then(() => {
        const loginUrl = `${window.location.origin}/login`;
        const blurb = [
          `Patient Portal Login`,
          ``,
          `URL: ${loginUrl}`,
          `  → Select the "Patient" tab on the login page`,
          `Patient ID: ${shareLoginPatient.patient_uuid}`,
          `Password: ${shareLoginPassword}`,
          ``,
          `Please keep this information secure and do not share it with others.`,
        ].join('\n');
        navigator.clipboard.writeText(blurb).then(() => {
          setShareLoginCopied(true);
        });
      })
      .catch((e: any) => setShareLoginErr(e?.data?.message || e?.message || 'Failed to reset password'))
      .finally(() => setShareLoginSubmitting(false));
  };

  const handleCreateSubAccount = (e: React.FormEvent) => {
    e.preventDefault();
    setCreateErr('');
    if (!createUsername.trim() || !createFirstName.trim() || !createLastName.trim() || !createPassword.trim()) {
      setCreateErr('Username, first name, last name, and password are required.');
      return;
    }
    setCreateSubmitting(true);
    api
      .createSubAccount({
        username: createUsername.trim(),
        first_name: createFirstName.trim(),
        last_name: createLastName.trim(),
        password: createPassword,
      })
      .then(() => {
        setCreateUsername('');
        setCreateFirstName('');
        setCreateLastName('');
        setCreatePassword('');
        setCreateRoles('');
        setCreateGroups('');
        setAddUserOpen(false);
        refreshSubAccounts();
      })
      .catch((e: any) => {
        setCreateErr(e?.data?.message || e?.message || 'Failed to create sub-account');
      })
      .finally(() => setCreateSubmitting(false));
  };

  const handleResetSubAccountPassword = (userId: number, username: string) => {
    const newPw = window.prompt(`New password for "${username}" (min 8 chars):`);
    if (!newPw) return;
    if (newPw.length < 8) { alert('Password must be at least 8 characters.'); return; }
    setResettingPwId(userId);
    api.adminResetPassword(userId, newPw)
      .then((res) => {
        if (res?.success) alert(`Password reset for "${username}".`);
        else alert(res?.message || 'Reset failed.');
      })
      .catch((e: any) => alert(e?.data?.message || e?.message || 'Reset failed.'))
      .finally(() => setResettingPwId(null));
  };

  const handleRemoveSubAccount = (userId: number, username: string) => {
    if (!window.confirm(`Remove sub-account "${username}" (ID ${userId})? This cannot be undone.`)) return;
    setRemovingId(userId);
    setErr('');
    api
      .removeSubAccount(userId)
      .then((res) => {
        if (res?.success) {
          setSubAccounts((prev) => prev.filter((u) => u.id !== userId));
        } else {
          setErr(res?.message || 'Failed to remove sub-account');
        }
      })
      .catch((e: any) => setErr(e?.data?.message || e?.message || 'Failed to remove sub-account'))
      .finally(() => setRemovingId(null));
  };

  const isGroupAdmin = tiersMe?.is_group_admin === true;
  const maxSub = tiersMe?.max_sub_accounts ?? 0;
  const currentSub = tiersMe?.sub_account_count ?? subAccounts.length;
  // Show Add User when tier allows sub-accounts (Small Business / Enterprise), even if is_group_admin not yet set in DB
  const canCreateMore = maxSub > 0 && currentSub < maxSub;
  const showUsersTabActions = maxSub > 0;

  const filteredUsers = subAccounts.filter(
    (u) =>
      !searchQuery ||
      [u.first_name, u.last_name, u.username].some((s) =>
        String(s).toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const isAdminRole = tiersMe?.user_role !== 'sub_account';
  const showPatientPortal = (tiersMe?.is_group_admin || tiersMe?.user_group_id != null);
  const tabs: { id: TabId; label: string }[] = [
    { id: 'account', label: 'Account' },
    ...(isAdminRole
      ? [
          { id: 'users' as TabId, label: 'Users' },
          { id: 'groups' as TabId, label: 'Groups' },
          { id: 'system' as TabId, label: 'System' },
        ]
      : []),
    ...(showPatientPortal ? [{ id: 'patient_portal' as TabId, label: 'Patient Portal' }] : []),
    { id: 'agent_local', label: 'Local Agent' },
  ];

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <>
      <TabsRow>
        {tabs.map((t) => (
          <Tab
            key={t.id}
            type="button"
            $active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </Tab>
        ))}
      </TabsRow>

      <SettingsContent>
        {err && <p style={{ color: '#b91c1c', marginBottom: 16 }}>{err}</p>}

        {activeTab === 'account' && (
          <>
            <PageTitle>Account</PageTitle>
            {tiersMe && (
              <section>
                <p style={{ marginBottom: 8 }}>
                  <strong>Tier:</strong> {{
                    free: 'Free',
                    individual: 'Individual Clinician',
                    small_business: 'Small Business',
                    enterprise: 'Enterprise',
                  }[tiersMe.tier] ?? tiersMe.tier}
                </p>
              </section>
            )}
            {tokenStatus && (
              <section style={{ marginTop: 16, padding: '16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <p style={{ fontWeight: 600, marginBottom: 10, fontSize: '0.9375rem' }}>AI Token Allowance</p>
                {tokenStatus.exceeded && (
                  <p style={{ color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>
                    Monthly allowance exhausted. Please contact support to top up.
                  </p>
                )}
                {!tokenStatus.exceeded && tokenStatus.warning && (
                  <p style={{ color: '#d97706', fontWeight: 600, marginBottom: 8 }}>
                    Over 80% of monthly allowance used.
                  </p>
                )}
                <div style={{ marginBottom: 8, fontSize: '0.8125rem', color: '#374151' }}>
                  <span>
                    Included: ${parseFloat(tokenStatus.balance.included_used_usd).toFixed(4)} / ${parseFloat(tokenStatus.balance.included_usd).toFixed(2)} USD
                  </span>
                  <span style={{ margin: '0 12px', color: '#d1d5db' }}>|</span>
                  <span>Top-up balance: ${parseFloat(tokenStatus.balance.topup_balance_usd).toFixed(2)}</span>
                  {tokenStatus.balance.allowance_reset_date && (
                    <>
                      <span style={{ margin: '0 12px', color: '#d1d5db' }}>|</span>
                      <span>Resets: {tokenStatus.balance.allowance_reset_date}</span>
                    </>
                  )}
                </div>
                <div style={{ background: '#e5e7eb', borderRadius: 4, height: 8, marginBottom: 12 }}>
                  <div
                    style={{
                      width: `${Math.min(tokenStatus.balance.percent_used, 100)}%`,
                      height: '100%',
                      borderRadius: 4,
                      background: tokenStatus.exceeded ? '#dc2626' : tokenStatus.warning ? '#d97706' : '#2563eb',
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  To add credits, contact support.
                </p>
              </section>
            )}
          </>
        )}

        {/* Add User button and users table only on Users tab */}
        {activeTab === 'users' && isAdminRole && (
          <>
            <PageTitle>Users</PageTitle>
            <Toolbar>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <SearchWrap>
                  <SearchIcon />
                  <SearchInput
                    type="search"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchWrap>
                <BtnSecondary type="button" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Filter +
                </BtnSecondary>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {showUsersTabActions && (
                  <QuotaBadge>
                    Full Users {currentSub}/{maxSub}
                  </QuotaBadge>
                )}
                {showUsersTabActions && (
                  <AddUserBtn
                    type="button"
                    onClick={() => setAddUserOpen(true)}
                    disabled={!canCreateMore}
                    title={!canCreateMore ? `Maximum sub-accounts (${maxSub}) reached` : 'Add user'}
                  >
                    <PlusIcon />
                    Add User
                  </AddUserBtn>
                )}
              </div>
            </Toolbar>

            {!showUsersTabActions ? (
              <p style={{ color: '#6b7280' }}>
                Sub-accounts are available for Small Business and Enterprise plans. Your account does not have sub-account capability.
              </p>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Username</Th>
                      <Th>Status</Th>
                      <Th>Job Title</Th>
                      <Th>Roles</Th>
                      <Th>User Type</Th>
                      <Th>User Access</Th>
                      <Th>MFA required</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <Tr>
                        <Td colSpan={9} style={{ textAlign: 'center', color: '#6b7280', padding: 32 }}>
                          {subAccounts.length === 0 ? 'No sub-accounts yet. Click Add User to create one.' : 'No users match your search.'}
                        </Td>
                      </Tr>
                    ) : (
                      filteredUsers.map((u) => (
                        <Tr key={u.id}>
                          <Td>
                            <span style={{ fontWeight: 500 }}>
                              {u.first_name} {u.last_name}
                            </span>
                          </Td>
                          <Td>{u.username}</Td>
                          <Td>
                            <StatusBadge $active>Active</StatusBadge>
                          </Td>
                          <Td>—</Td>
                          <Td>—</Td>
                          <Td>Internal</Td>
                          <Td>
                            <AccessBadge>Full</AccessBadge>
                          </Td>
                          <Td>
                            {u.is_mfa_required === false ? (
                              <span style={{ color: '#6b7280' }}>No</span>
                            ) : (
                              <span style={{ color: '#111827', fontWeight: 500 }}>Yes</span>
                            )}
                          </Td>
                          <Td>
                            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => handleResetSubAccountPassword(u.id, u.username)}
                                disabled={resettingPwId === u.id}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  cursor: resettingPwId === u.id ? 'wait' : 'pointer',
                                  color: '#1d4ed8',
                                  background: 'none',
                                  border: '1px solid #93c5fd',
                                  borderRadius: 4,
                                }}
                              >
                                {resettingPwId === u.id ? 'Resetting…' : 'Reset Pwd'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveSubAccount(u.id, u.username)}
                                disabled={removingId === u.id}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  cursor: removingId === u.id ? 'wait' : 'pointer',
                                  color: '#b91c1c',
                                  background: 'none',
                                  border: '1px solid #fca5a5',
                                  borderRadius: 4,
                                }}
                              >
                                {removingId === u.id ? 'Removing…' : 'Remove'}
                              </button>
                            </span>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </>
        )}

        {activeTab === 'groups' && isAdminRole && (
          <GroupsTab />
        )}

        {activeTab === 'system' && isAdminRole && (
          <>
            <PageTitle>System</PageTitle>
            <p style={{ color: '#6b7280' }}>System settings. (Coming soon.)</p>
          </>
        )}

        {activeTab === 'agent_local' && (
          <>
            <PageTitle>Local Agent</PageTitle>
            <p style={{ color: '#6b7280', marginBottom: 20, maxWidth: 560 }}>
              The DrSchmoe Agent runs on your Mac and connects directly to lab portals using your local Chrome browser.
              Install it, open it, and paste your pairing token below.
            </p>

            <AgentCard>
              <AgentCardHeader>
                <AgentDot $online={agentStatus?.online ?? false} />
                <AgentStatusLabel $online={agentStatus?.online ?? false}>
                  {agentLoading && !agentStatus
                    ? 'Checking…'
                    : agentStatus?.online
                    ? `Connected · ${agentStatus.platform || 'macOS'} · v${agentStatus.agent_version}`
                    : 'Not connected'}
                </AgentStatusLabel>
              </AgentCardHeader>

              <AgentCardBody>
                <AgentRow>
                  <label>Pairing token</label>
                  {agentStatus?.token ? (
                    <TokenRow>
                      <TokenBox>{agentStatus.token}</TokenBox>
                      <IconBtn
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(agentStatus.token!);
                          setTokenCopied(true);
                          setTimeout(() => setTokenCopied(false), 2000);
                        }}
                      >
                        {tokenCopied ? 'Copied!' : 'Copy'}
                      </IconBtn>
                      <IconBtn
                        type="button"
                        disabled={regenerating}
                        onClick={() => {
                          if (!window.confirm('Regenerate token? The agent will disconnect and need re-pairing.')) return;
                          setRegenerating(true);
                          api.regenerateMcpToken()
                            .then((res) => {
                              setAgentStatus((prev) => prev ? { ...prev, token: res.token } : prev);
                            })
                            .catch(() => {})
                            .finally(() => setRegenerating(false));
                        }}
                      >
                        {regenerating ? 'Regenerating…' : 'Regenerate'}
                      </IconBtn>
                    </TokenRow>
                  ) : (
                    <AgentMeta>Loading token…</AgentMeta>
                  )}
                </AgentRow>

                {agentStatus?.last_seen && (
                  <AgentMeta>Last seen: {new Date(agentStatus.last_seen).toLocaleString()}</AgentMeta>
                )}

                <AgentRow>
                  <label>Install the agent</label>
                  <AgentDownloadBtn
                    disabled={downloading}
                    onClick={() => {
                      setDownloading(true);
                      api.getMcpAgentDownloadUrl()
                        .then((res) => { window.location.href = res.url; })
                        .catch(() => {})
                        .finally(() => setDownloading(false));
                    }}
                  >
                    {downloading ? 'Preparing…' : '↓ Download DrSchmoe Agent (macOS)'}
                  </AgentDownloadBtn>
                  <AgentMeta>
                    After installing: open the app, click "Set Pairing Token" in the menu bar, and paste the token above.
                  </AgentMeta>
                </AgentRow>
              </AgentCardBody>
            </AgentCard>
          </>
        )}
        {activeTab === 'patient_portal' && showPatientPortal && (
          <>
            <PageTitle>Patient Portal</PageTitle>
            <p style={{ color: '#6b7280', marginBottom: 20, maxWidth: 600 }}>
              Manage patient accounts for your group. Each patient receives a Patient ID to log in to the patient-facing portal.
            </p>
            {patientErr && <p style={{ color: '#b91c1c', marginBottom: 12 }}>{patientErr}</p>}
            <div style={{ marginBottom: 16 }}>
              <BtnPrimary type="button" onClick={() => setAddPatientOpen(true)}>+ Add Patient</BtnPrimary>
            </div>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>DOB</th>
                    <th>Patient ID</th>
                    <th>MFA</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.length === 0 && (
                    <tr><td colSpan={5} style={{ color: '#9ca3af', textAlign: 'center', padding: '24px 0' }}>No patients yet</td></tr>
                  )}
                  {patients.map((p) => (
                    <tr key={p.patient_uuid}>
                      <td>{p.first_name} {p.last_name}</td>
                      <td>{p.dob}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.patient_uuid}</td>
                      <td>{p.mfa_enabled ? 'On' : 'Off'}</td>
                      <td>
                        <BtnSecondary
                          type="button"
                          style={{ marginRight: 8, fontSize: 13, padding: '4px 10px' }}
                          onClick={() => handleOpenShareLogin(p)}
                        >
                          Share Login
                        </BtnSecondary>
                        <BtnSecondary
                          type="button"
                          style={{ color: '#b91c1c', fontSize: 13, padding: '4px 10px' }}
                          disabled={removingPatientUuid === p.patient_uuid}
                          onClick={() => handleRemovePatient(p.patient_uuid, `${p.first_name} ${p.last_name}`)}
                        >
                          {removingPatientUuid === p.patient_uuid ? '…' : 'Remove'}
                        </BtnSecondary>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </>
        )}
      </SettingsContent>

      {addPatientOpen && (
        <ModalOverlay onClick={() => !patientCreateSubmitting && setAddPatientOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Add Patient</ModalTitle>
              <ModalClose type="button" onClick={() => !patientCreateSubmitting && setAddPatientOpen(false)}>×</ModalClose>
            </ModalHeader>
            <form onSubmit={handleCreatePatient}>
              <ModalBody>
                <FormRow>
                  <label>First Name *</label>
                  <input type="text" value={patientFirstName} onChange={(e) => setPatientFirstName(e.target.value)} placeholder="First name" />
                </FormRow>
                <FormRow>
                  <label>Last Name *</label>
                  <input type="text" value={patientLastName} onChange={(e) => setPatientLastName(e.target.value)} placeholder="Last name" />
                </FormRow>
                <FormRow>
                  <label>Date of Birth *</label>
                  <input type="text" value={patientDob} onChange={(e) => setPatientDob(e.target.value)} placeholder="MM/DD/YYYY" />
                </FormRow>
                <FormRow>
                  <label>Password *</label>
                  <input type="text" value={patientPassword} onChange={(e) => setPatientPassword(e.target.value)} placeholder="Initial password" />
                </FormRow>
                {patientCreateErr && <p style={{ color: '#b91c1c', fontSize: 14, marginTop: 8 }}>{patientCreateErr}</p>}
              </ModalBody>
              <ModalFooter>
                <BtnSecondary type="button" onClick={() => setAddPatientOpen(false)} disabled={patientCreateSubmitting}>Cancel</BtnSecondary>
                <BtnPrimary type="submit" disabled={patientCreateSubmitting}>{patientCreateSubmitting ? 'Saving…' : 'Add Patient'}</BtnPrimary>
              </ModalFooter>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}

      {shareLoginPatient && (
        <ModalOverlay onClick={() => !shareLoginSubmitting && setShareLoginPatient(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Share Login — {shareLoginPatient.first_name} {shareLoginPatient.last_name}</ModalTitle>
              <ModalClose type="button" onClick={() => !shareLoginSubmitting && setShareLoginPatient(null)}>×</ModalClose>
            </ModalHeader>
            <ModalBody>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                Set a new password and copy the login instructions to send to the patient.
              </p>
              <FormRow>
                <label>Patient ID</label>
                <input type="text" readOnly value={shareLoginPatient.patient_uuid} style={{ fontFamily: 'monospace', fontSize: 12, background: '#f9fafb' }} />
              </FormRow>
              <FormRow>
                <label>New Password</label>
                <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                  <input
                    type="text"
                    value={shareLoginPassword}
                    onChange={(e) => { setShareLoginPassword(e.target.value); setShareLoginCopied(false); }}
                    style={{ flex: 1 }}
                  />
                  <BtnSecondary type="button" style={{ fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }} onClick={() => { setShareLoginPassword(genPassword()); setShareLoginCopied(false); }}>
                    Regenerate
                  </BtnSecondary>
                </div>
              </FormRow>
              {shareLoginErr && <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{shareLoginErr}</p>}
              {shareLoginCopied && (
                <p style={{ color: '#16a34a', fontSize: 13, marginTop: 8 }}>
                  Copied! Paste and send to the patient.
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <BtnSecondary type="button" onClick={() => setShareLoginPatient(null)} disabled={shareLoginSubmitting}>Close</BtnSecondary>
              <BtnPrimary type="button" disabled={shareLoginSubmitting || !shareLoginPassword} onClick={handleShareLoginSubmit}>
                {shareLoginSubmitting ? 'Saving…' : shareLoginCopied ? 'Copied!' : 'Reset Password & Copy Login Info'}
              </BtnPrimary>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {addUserOpen && (
        <ModalOverlay onClick={() => !createSubmitting && setAddUserOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Add User</ModalTitle>
              <ModalClose type="button" onClick={() => !createSubmitting && setAddUserOpen(false)}>
                ×
              </ModalClose>
            </ModalHeader>
            <form onSubmit={handleCreateSubAccount}>
              <ModalBody>
                <FormRow>
                  <label>Username *</label>
                  <input
                    type="text"
                    value={createUsername}
                    onChange={(e) => setCreateUsername(e.target.value)}
                    required
                    placeholder="e.g. jeremy.schmoe"
                  />
                </FormRow>
                <FormRow>
                  <label>First name *</label>
                  <input
                    type="text"
                    value={createFirstName}
                    onChange={(e) => setCreateFirstName(e.target.value)}
                    required
                    placeholder="First name"
                  />
                </FormRow>
                <FormRow>
                  <label>Last name *</label>
                  <input
                    type="text"
                    value={createLastName}
                    onChange={(e) => setCreateLastName(e.target.value)}
                    required
                    placeholder="Last name"
                  />
                </FormRow>
                <FormRow>
                  <label>Password *</label>
                  <input
                    type="password"
                    value={createPassword}
                    onChange={(e) => setCreatePassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Min 8 characters"
                  />
                </FormRow>
                <FormRow>
                  <label>User Access</label>
                  <select value={createUserAccess} onChange={(e) => setCreateUserAccess(e.target.value)}>
                    <option value="Full">Full</option>
                    <option value="Lite">Lite</option>
                  </select>
                </FormRow>
                <FormRow>
                  <label>Roles</label>
                  <input
                    type="text"
                    value={createRoles}
                    onChange={(e) => setCreateRoles(e.target.value)}
                    placeholder="e.g. Clinician, Manager"
                  />
                </FormRow>
                <FormRow>
                  <label>Groups</label>
                  <input
                    type="text"
                    value={createGroups}
                    onChange={(e) => setCreateGroups(e.target.value)}
                    placeholder="Assign to groups"
                  />
                </FormRow>
                {createErr && <p style={{ color: '#b91c1c', fontSize: 14, marginTop: 8 }}>{createErr}</p>}
              </ModalBody>
              <ModalFooter>
                <BtnSecondary type="button" onClick={() => setAddUserOpen(false)} disabled={createSubmitting}>
                  Cancel
                </BtnSecondary>
                <BtnPrimary type="submit" disabled={createSubmitting}>
                  {createSubmitting ? 'Saving…' : 'Save changes'}
                </BtnPrimary>
              </ModalFooter>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
};

export default Settings;
